<script setup lang="ts">
import { watch, onMounted, ref, computed } from 'vue'
import { Icon } from '@iconify/vue'
import type { IPolicy } from '@/types/asicw/policies'
import AsicwPolicySearchCard from './AsicwPolicySearchCard.vue'
import { usePolicySelect } from '@/composables/asicw/policy-select'
import { usePolicies } from '@/composables/asicw/policies'
import { PolicyStatus } from '@/types/asicw/policies'
import { reactive } from 'vue'

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    placeholder?: string
    excludedPolicyIds?: number[]
  }>(),
  {
    disabled: false,
    placeholder: 'Search by policy number, SN or model',
    excludedPolicyIds: () => [],
  }
)

const selectedPolicy = defineModel<IPolicy | undefined>()
const { policyOptions, updatePolicyOptions, clearPolicyOptions } = usePolicySelect()
const { getPoliciesQuery } = usePolicies()

const availablePolicyOptions = computed(() =>
  policyOptions.value.filter((option) => !props.excludedPolicyIds.includes(option.value.id))
)

const searchQuery = ref<string>()
const filters = reactive({
  page: 1,
  per_page: 100,
  status: PolicyStatus.Active,
  fragment: searchQuery.value,
})

const { data, isLoading } = getPoliciesQuery(filters)

watch(data, (response) => {
  if (response?.data) {
    updatePolicyOptions(response.data)
  } else {
    clearPolicyOptions()
  }
})

onMounted(() => {
  if (data.value) updatePolicyOptions(data.value.data)
})

const handleSearch = (query?: string) => {
  searchQuery.value = query?.trim()
  filters.fragment = searchQuery.value
}

watch(selectedPolicy, (policy) => {
  if (!policy) {
    selectedPolicy.value = undefined
    return
  }

  if (!policyOptions.value.length) {
    policyOptions.value.push({ value: policy, label: `Policy #${policy.id}` })
  }

  const preSelectedOption = policyOptions.value.find((option) => option.value.id === policy.id)
  selectedPolicy.value = preSelectedOption?.value ?? policy
})

// Load initial policies
onMounted(() => {
  handleSearch()
})
</script>

<template>
  <el-select-v2
    v-model="selectedPolicy"
    :options="availablePolicyOptions"
    filterable
    remote
    :remote-method="handleSearch"
    clearable
    :loading="isLoading"
    no-data-text="No policies found"
    :placeholder="props.placeholder"
    :item-height="96"
    value-key="id"
    :disabled="disabled"
  >
    <template #default="{ item }">
      <AsicwPolicySearchCard :policy="item.value" />
    </template>
    <template #loading>
      <div class="remote-search--loading">
        <Icon icon="eos-icons:three-dots-loading" height="20" />
      </div>
    </template>
  </el-select-v2>
</template>

<style scoped lang="scss">
.remote-search--loading {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
